import React from 'react';
import { cva } from 'class-variance-authority';

const tabButton = cva(
  'flex cursor-pointer items-center gap-1.5 border-x-0 border-t-0 border-b-2 border-solid bg-transparent px-0 pb-1 pt-0 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      active: {
        true: 'border-gray-900 font-semibold text-gray-900',
        false: 'border-transparent text-gray-500 hover:text-gray-900',
      },
    },
  },
);

type KoobTabsCompactTab = {
  id: string;
  label: React.ReactNode;
  /** Font-awesome class string (e.g. "fa-light fa-map"). */
  icon?: string;
  disabled?: boolean;
};

type KoobTabsCompactProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className' | 'onChange'
> & {
  tabs: KoobTabsCompactTab[];
  value: string;
  onValueChange: (id: string) => void;
};

/**
 * Compact text tabs for card headers (underline on the active tab) — the
 * small sibling of KoobTabs, which renders full-width page tabs.
 */
const KoobTabsCompact = React.forwardRef<HTMLDivElement, KoobTabsCompactProps>(
  ({ tabs, value, onValueChange, ...props }, ref) => (
    <div ref={ref} className="flex items-center gap-4" {...props}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          disabled={tab.disabled}
          onClick={() => onValueChange(tab.id)}
          className={tabButton({ active: value === tab.id })}
        >
          {tab.icon && <i className={tab.icon} aria-hidden="true" />}
          {tab.label}
        </button>
      ))}
    </div>
  ),
);

KoobTabsCompact.displayName = 'KoobTabsCompact';

export default KoobTabsCompact;
